import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { ActivityTimeline } from "@magnetic/activity-timeline";
import { Text } from "@magnetic/text";
import { compareDesc } from "date-fns";

import { EmptyContainer } from "src/components/emptyContainer";
import { HFlex } from "src/components/hFlex";
import { PageLoader } from "src/components/pageLoader";
import { useTextSearch } from "src/components/tables/filters/useTextSearch";
import { VFlex } from "src/components/vFlex";
import { TimeZoneContext } from "src/core/timeZoneProvider";
import { FORMAT } from "src/dataTypes/dateFormats";
import { ActivityEvent } from "src/gen/schema/configd/api";
import {
  objectTypeToJSON,
  operationToJSON
} from "src/gen/schema/models/global";

import type { BaseCandidate } from "./candidateTypes";

import "./previewChanges.scss";

interface PreviewChangesProps<T extends BaseCandidate> {
  readonly candidate: T | undefined;
  readonly loading?: boolean;
}

const buildDisplayString = (
  operation: string,
  type: string,
  objectName: string
) => `${operation} ${type} "${objectName}"`;

export const PreviewChanges = <T extends BaseCandidate>({
  candidate,
  loading = false
}: PreviewChangesProps<T>) => {
  const { getFormattedDate } = useContext(TimeZoneContext);

  const generateKey = useCallback(
    (username: string, ts: Date | 0 = 0) => {
      return `${username}|${getFormattedDate(ts, FORMAT.FULL)}`;
    },
    [getFormattedDate]
  );

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    candidate?.events.reduce((acc, e) => {
      acc[generateKey(e.username, e.timestamp)] = true;
      return acc;
    }, state);
    return state;
  });

  useEffect(() => {
    setExpanded((prev) => {
      const updated = candidate?.events.reduce(
        (acc, e) => {
          acc[generateKey(e.username, e.timestamp)] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      return { ...updated, ...prev };
    });
  }, [candidate?.events, generateKey]);

  const { filteredData, textSearchBar, ref } = useTextSearch<ActivityEvent>(
    candidate?.events,
    (event: ActivityEvent, query: string) => {
      const {
        operation,
        objectName,
        objectType,
        username,
        timestamp = 0
      } = event;
      const searchStr = query.toLowerCase();
      const oper = operationToJSON(operation);
      const type = objectTypeToJSON(objectType).replaceAll("_", " ");

      const data = buildDisplayString(oper, type, objectName);
      const key: string = generateKey(username, timestamp);

      return `${key} ${data}`.toLowerCase().includes(searchStr);
    }
  );

  const items = useMemo(() => {
    if (candidate) {
      const activityEventsMap = new Map<string, ActivityEvent[]>();

      /**
       * Need to sort the events by date and user.
       * That way we can group changes the same
       * way they are displayed in the mockup.
       */
      filteredData
        .toSorted((a: ActivityEvent, b: ActivityEvent) => {
          return compareDesc(a.timestamp ?? 0, b.timestamp ?? 0);
        })
        /**
         * Need to now group the events by date and user.
         * using foreach to populate a map. The key for
         * The map will be a string "username|timestamp".
         */
        .forEach((e: ActivityEvent) => {
          const key: string = generateKey(e.username, e.timestamp);
          const tmp = activityEventsMap.get(key) ?? [];
          activityEventsMap.set(key, [...tmp, e]);
        });

      /**
       * iterate over the map to generate the
       * ActivityTimeline Items and the details
       * of each provisioning event
       */
      const elements: React.ReactElement[] = [];
      activityEventsMap.forEach((v: ActivityEvent[], k: string) => {
        const [user, ts] = k.split("|");
        const activityEvents = v.map((e: ActivityEvent) => {
          const operation = operationToJSON(e.operation).toLowerCase();
          const type = objectTypeToJSON(e.objectType)
            .replaceAll("_", " ")
            .toLowerCase();

          return (
            <Text
              className="transaction-event"
              key={`${candidate.txnId}-${type}-${operation}-${e.objectName}-${e.objectId}`}
            >
              {buildDisplayString(operation, type, e.objectName)}
            </Text>
          );
        });

        /**
         * ActivityTimeline is not very flexible,
         * I basically need to put all the data in
         * to the header element.
         */
        const content = (
          <VFlex gap="sm">
            <HFlex
              justify="space-between"
              align="center"
              className="transaction-title"
            >
              <Text weight="semi-bold" className="created-by">
                {user}
              </Text>
              <Text weight="regular">{ts}</Text>
            </HFlex>
          </VFlex>
        );

        elements.push(
          <ActivityTimeline.Item
            open={expanded[k]}
            key={`${user}${ts}`}
            heading={content}
            status="neutral"
            nextStatus="neutral"
            time={-1}
          >
            <VFlex className="transaction-event-list" gap="sm">
              {activityEvents}
            </VFlex>
          </ActivityTimeline.Item>
        );
      });
      if (elements?.length) {
        return elements;
      }
    }
    return <EmptyContainer text="No changes pending" />;
  }, [candidate, filteredData, expanded, generateKey]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <VFlex gap="xs">
      <div className="search-box">{textSearchBar}</div>
      <div ref={ref} className="preview-changes">
        <ActivityTimeline className="highlight-text-for-search">
          {items}
        </ActivityTimeline>
      </div>
    </VFlex>
  );
};
